import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState, } from 'react';
import clsx from 'clsx';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { PostStatus } from '@tribeplatform/gql-client/types';
import { useRouter } from '@tribeplatform/react-sdk';
import { useAddReply, useAuthMember, usePost, useSpaceUtil, } from '@tribeplatform/react-sdk/hooks';
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { useCachedMentionsFromPost } from '../common/hooks/useCachedMentionsFromPost';
import { useLogin } from '../common/hooks/useLogin';
import { getMappingFields } from '../common/utils/post';
import { useCanQuitEditor } from '../Composer/utils/state';
import { T, useI18n } from '../i18n';
import { ReplyEditor } from '../Reply/ReplyEditor';
import { confirmActionOnLockedPost } from './utils';
export const ReplyBar = forwardRef(({ post, defaultExpand = false }, ref) => {
    var _a, _b, _c, _d, _e;
    const intl = useI18n();
    const { Link } = useRouter();
    const containerRef = useRef();
    const [expanded, setExpanded] = useState(defaultExpand);
    const editorRef = useRef(null);
    const { data: member } = useAuthMember();
    const { mutate: addReply, isLoading } = useAddReply();
    const { isLoggedIn, showLogin } = useLogin();
    const { generateProfilePath } = useSpaceUtil();
    const canQuitEditor = useCanQuitEditor();
    const picture = member === null || member === void 0 ? void 0 : member.profilePicture;
    const { data: fullPost, isFetching: fetchingFullPost } = usePost({
        variables: { id: post.id },
        useQueryOptions: {
            enabled: expanded,
        },
    });
    const cachedMentions = useCachedMentionsFromPost(fullPost === null || fullPost === void 0 ? void 0 : fullPost.id, {
        enabled: expanded,
    });
    const loading = isLoading || fetchingFullPost;
    const availableReplyTypes = ((_a = fullPost === null || fullPost === void 0 ? void 0 : fullPost.authMemberProps) === null || _a === void 0 ? void 0 : _a.availableReplyTypes) ||
        ((_b = post === null || post === void 0 ? void 0 : post.authMemberProps) === null || _b === void 0 ? void 0 : _b.availableReplyTypes);
    useImperativeHandle(ref, () => ({
        scrollIntoView: () => {
            var _a, _b;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
            });
            (_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        },
        expand: setExpanded,
    }));
    const quit = useCallback(() => {
        setExpanded(false);
    }, []);
    const cancel = useCallback(isTouched => {
        if (canQuitEditor({
            isTouched,
        })) {
            quit();
        }
    }, [canQuitEditor, quit]);
    const onReply = useCallback(async (content = '', editor) => {
        const { isEmpty: noText } = editor;
        const isEmpty = noText;
        if (loading || isEmpty || content === '') {
            return;
        }
        const postType = availableReplyTypes === null || availableReplyTypes === void 0 ? void 0 : availableReplyTypes[0];
        if (!(postType === null || postType === void 0 ? void 0 : postType.id)) {
            return;
        }
        const mappingFields = getMappingFields({
            content,
            postTypeName: postType.name,
        });
        addReply({
            postId: post.id,
            input: {
                postTypeId: postType.id,
                mappingFields,
                publish: true,
            },
        }, {
            onSuccess: data => {
                if (data.status === PostStatus.BLOCKED) {
                    toast({
                        title: intl.$t({
                            id: 'Generics.PendingReview',
                            defaultMessage: 'Pending review',
                        }),
                        description: intl.$t({
                            id: 'Post.Replybar.SuccessToastDescription',
                            defaultMessage: 'Your reply will be visible to others once it’s been reviewed by a moderator.',
                        }),
                        status: 'info',
                    });
                }
                quit();
            },
            onError: error => {
                var _a, _b, _c;
                const message = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.errors) === null || _b === void 0 ? void 0 : _b.map(e => e.message)) === null || _c === void 0 ? void 0 : _c.join('\n');
                toast({
                    title: intl.$t({
                        id: 'Post.Replybar.ReplyFailedTitle',
                        defaultMessage: 'Reply failed',
                    }),
                    description: message ||
                        intl.$t({
                            id: 'Generics.SomethingWentWrongTryAgain',
                            defaultMessage: 'Something went wrong, please try again.',
                        }),
                    status: 'error',
                });
            },
        });
    }, [loading, availableReplyTypes, addReply, post.id, quit, intl]);
    const repliedToCount = (_c = post.repliedToIds) === null || _c === void 0 ? void 0 : _c.length;
    // If it is not a reply to a reply, show the avatar below
    const withAvatar = !repliedToCount || repliedToCount > 1;
    const containerClasses = clsx(['flex space-s-4 bg-surface']);
    if (fullPost) {
        const [canCreateReply] = hasScopesPermission(post, ['createReply']);
        if ((isLoggedIn && !canCreateReply) || !(availableReplyTypes === null || availableReplyTypes === void 0 ? void 0 : availableReplyTypes.length)) {
            return null;
        }
    }
    if (expanded) {
        return (_jsxs("div", { className: containerClasses, ref: containerRef, children: [withAvatar && (_jsx("div", { children: _jsx(Link, { variant: "inherit", href: generateProfilePath(), className: "flex w-12 flex-shrink-0", children: _jsx(Avatar, { name: member === null || member === void 0 ? void 0 : member.name, size: "lg", src: ((_d = picture === null || picture === void 0 ? void 0 : picture.urls) === null || _d === void 0 ? void 0 : _d.thumb) || (picture === null || picture === void 0 ? void 0 : picture.url) }, void 0) }, void 0) }, void 0)), _jsx("div", { className: "flex flex-col bg-surface flex-grow max-w-full min-w-0", children: _jsx("div", { className: clsx({
                            'text-content-subdued': loading,
                        }), children: _jsx(ReplyEditor, { onReply: onReply, ref: editorRef, onCancel: cancel, cachedMentions: cachedMentions, isLoading: loading }, void 0) }, void 0) }, void 0)] }, void 0));
    }
    return (_jsxs("div", { className: containerClasses, children: [withAvatar && (_jsx(Link, { variant: "inherit", href: generateProfilePath(), className: "flex flex-shrink-0 w-12 h-12 hover:opacity-90", children: _jsx(Avatar, { name: member === null || member === void 0 ? void 0 : member.name, size: "lg", src: ((_e = picture === null || picture === void 0 ? void 0 : picture.urls) === null || _e === void 0 ? void 0 : _e.thumb) || (picture === null || picture === void 0 ? void 0 : picture.url) }, void 0) }, void 0)), _jsx("button", { type: "button", onClick: async (e) => {
                    if (!isLoggedIn) {
                        e.preventDefault();
                        return showLogin();
                    }
                    if (!(await confirmActionOnLockedPost(intl)(post))) {
                        return;
                    }
                    setExpanded(true);
                    setTimeout(() => {
                        var _a;
                        (_a = editorRef === null || editorRef === void 0 ? void 0 : editorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 300);
                }, className: "cursor-pointer flex-grow rounded-md px-2.5 mt-1 h-10 flex items-center bg-surface-subdued hover:bg-surface-hovered text-content-subdued hover:text-content-hovered", children: _jsx(T, { id: "Post.ReplyBar.ReplyButtonText", defaultMessage: "What are your thoughts?" }, void 0) }, void 0)] }, void 0));
});
//# sourceMappingURL=ReplyBar.js.map